import { DateFormatter } from '@repo/toolkit/date'
import { isFuture, isPast, parseISO, startOfDay } from 'date-fns'

type TripSchedule = {
  code: string // or `id: string` if that's your key
  time: { hours: number; minutes: number }
  timeZone: string
}

function toParisHHMM(hours: number, minutes: number, fromTz: string) {
  return DateFormatter.convertTimeZone({ hours, minutes }, fromTz, 'Europe/Paris')
}

function hhmmToMinutes(hhmm: string) {
  return DateFormatter.timeStringToMinutes(hhmm)
}

function checkIsPast(dateString: string | null | undefined): boolean {
  if (!dateString) return false
  return isPast(startOfDay(parseISO(dateString)))
}

function checkIsFuture(dateString: string | null | undefined): boolean {
  if (!dateString) return false
  return isFuture(startOfDay(parseISO(dateString)))
}

export function markNextRow(
  data: TripSchedule[],
  setMarkedRow: (v: string | null) => void,
  filterDate?: string | null
) {
  // If the date is in the past, don't show any arrow
  if (checkIsPast(filterDate)) {
    setMarkedRow(null)
    return
  }

  // If the date is in the future, show the arrow at the first row
  if (checkIsFuture(filterDate)) {
    setMarkedRow(data.length > 0 ? data[0].code : null)
    return
  }

  // If the date is today, find the closest time after now
  const now = new Date()
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone

  const parisNow = toParisHHMM(now.getHours(), now.getMinutes(), localTz)
  const parisNowMin = hhmmToMinutes(parisNow)

  // Keep the best candidate with its computed minutes to avoid rework
  let best: { row: TripSchedule; minutes: number } | null = null

  for (const row of data) {
    const rowHHMM = toParisHHMM(row.time.hours, row.time.minutes, row.timeZone)
    const rowMin = hhmmToMinutes(rowHHMM)

    if (rowMin >= parisNowMin) {
      if (!best || rowMin < best.minutes) {
        best = { row, minutes: rowMin }
      }
    }
  }

  setMarkedRow(best ? best.row.code : null)
}
